import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageIsAr, imageName, makeCanvas, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

// Helper: Checkerboard background for transparent areas
function drawCheckerboard(ctx, w, h) {
  const size = 16;
  ctx.fillStyle = '#f8fafc';
  ctx.fillRect(0, 0, w, h);
  ctx.fillStyle = '#e2e8f0';
  for (let y = 0; y < h; y += size) {
    for (let x = 0; x < w; x += size) {
      if ((Math.floor(x / size) + Math.floor(y / size)) % 2 === 0) {
        ctx.fillRect(x, y, size, size);
      }
    }
  }
}

// Micro-yield to prevent browser UI lockup and "Page isn't responding" dialog
const yieldThread = (ms = 16) => new Promise((resolve) => setTimeout(resolve, ms));

// Status & Progress updater on Engine Choice Cards
function updateEngineCardStatus(engine, pct, errorMsg) {
  const statusEl = document.getElementById(`engine-status-${engine}`);
  const progressEl = document.getElementById(`engine-progress-${engine}`);
  const progressBar = progressEl?.querySelector('.batch-choice-progress-bar');

  if (!statusEl) return;

  if (errorMsg) {
    statusEl.textContent = t('تعذر التنزيل (انقر للإعادة)', 'Download failed (tap to retry)');
    statusEl.setAttribute('data-state', 'error');
    if (progressEl) progressEl.style.display = 'none';
  } else if (pct >= 100) {
    statusEl.innerHTML = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" style="display:inline-block; vertical-align:-1px; margin-inline-end:3px;"><path d="M20 6L9 17l-5-5"/></svg> ${t('جاهز للعمل', 'Ready')}`;
    statusEl.setAttribute('data-state', 'ready');
    if (progressEl) progressEl.style.display = 'none';
  } else if (pct > 0) {
    statusEl.textContent = t(`جارٍ التجهيز... ${pct}%`, `Preparing... ${pct}%`);
    statusEl.setAttribute('data-state', 'downloading');
    if (progressEl) {
      progressEl.style.display = 'block';
      if (progressBar) progressBar.style.width = `${pct}%`;
    }
  } else {
    statusEl.textContent = t('جارٍ الاتصال...', 'Connecting...');
    statusEl.setAttribute('data-state', 'downloading');
    if (progressEl) {
      progressEl.style.display = 'block';
      if (progressBar) progressBar.style.width = '6%';
    }
  }
}

// ============================================================================
// Dedicated Web Worker & Paced Non-Blocking AI Execution
// ============================================================================
let bgWorker = null;
let currentJobId = 0;
const workerPromises = new Map();

function getBgWorker() {
  if (bgWorker) return bgWorker;
  if (typeof Worker === 'undefined') return null;
  try {
    bgWorker = new Worker('/assets/js/tools/background-remover-worker.bundle.js', { type: 'module' });
    bgWorker.onmessage = (e) => {
      const { type, id, progress, label, maskBuffer, width, height, error, engine } = e.data;
      if (type === 'PRELOAD_PROGRESS') {
        updateEngineCardStatus(engine, progress);
      } else if (type === 'PRELOAD_COMPLETE') {
        updateEngineCardStatus(engine, 100);
      } else if (type === 'PRELOAD_ERROR') {
        updateEngineCardStatus(engine, -1, error);
      } else if (type === 'PROGRESS') {
        const handler = workerPromises.get(id);
        if (handler?.onProgress) handler.onProgress(progress, label);
      } else if (type === 'COMPLETE') {
        const handler = workerPromises.get(id);
        if (handler) {
          workerPromises.delete(id);
          handler.resolve({ maskBuffer, width, height });
        }
      } else if (type === 'ERROR') {
        const handler = workerPromises.get(id);
        if (handler) {
          workerPromises.delete(id);
          handler.reject(new Error(error || 'Inference error'));
        }
      }
    };
    bgWorker.onerror = (err) => {
      console.warn('Background remover worker error:', err);
    };
    return bgWorker;
  } catch (err) {
    console.warn('Could not initialize module Worker, using in-thread fallback:', err);
    return null;
  }
}

// In-thread Fallback pipelines (Runs politely with numThreads = 1 and yielding if worker is unavailable)
let rmbgPipelinePromise = null;
let rmbgProgress = 0;
let modnetPipelinePromise = null;
let modnetProgress = 0;

async function getRmbgPipeline(onProgress) {
  if (rmbgPipelinePromise) {
    if (onProgress && rmbgProgress > 0) onProgress(rmbgProgress, t(`جارٍ تنزيل النموذج... ${rmbgProgress}%`, `Downloading model... ${rmbgProgress}%`));
    return rmbgPipelinePromise;
  }

  rmbgPipelinePromise = (async () => {
    try {
      const { AutoModel, AutoProcessor, env } = await import('@xenova/transformers');
      env.allowLocalModels = false;
      env.useBrowserCache = true;
      if (env.backends?.onnx?.wasm) {
        env.backends.onnx.wasm.wasmPaths = '/assets/vendor/transformers/';
        env.backends.onnx.wasm.numThreads = 1;
      }

      updateEngineCardStatus('ai-rmbg', 5);
      onProgress?.(10, t('جارٍ تهيئة المحرك بالذكاء الاصطناعي...', 'Initializing AI engine...'));

      const processor = await AutoProcessor.from_pretrained('briaai/RMBG-1.4');
      const model = await AutoModel.from_pretrained('briaai/RMBG-1.4', {
        config: { model_type: 'custom' },
        progress_callback: (p) => {
          if (p.status === 'progress' && Number.isFinite(p.progress)) {
            const pct = Math.round(p.progress);
            rmbgProgress = pct;
            updateEngineCardStatus('ai-rmbg', pct);
            onProgress?.(Math.round(15 + pct * 0.45), t(`جارٍ تنزيل نموذج الذكاء الاصطناعي... ${pct}%`, `Downloading AI model... ${pct}%`));
          }
        }
      });

      rmbgProgress = 100;
      updateEngineCardStatus('ai-rmbg', 100);
      return { model, processor };
    } catch (err) {
      rmbgPipelinePromise = null;
      rmbgProgress = 0;
      updateEngineCardStatus('ai-rmbg', -1, err.message);
      throw err;
    }
  })();

  return rmbgPipelinePromise;
}

async function getModnetPipeline(onProgress) {
  if (modnetPipelinePromise) {
    if (onProgress && modnetProgress > 0) onProgress(modnetProgress, t(`جارٍ تنزيل النموذج... ${modnetProgress}%`, `Downloading model... ${modnetProgress}%`));
    return modnetPipelinePromise;
  }

  modnetPipelinePromise = (async () => {
    try {
      const { AutoModel, AutoProcessor, env } = await import('@xenova/transformers');
      env.allowLocalModels = false;
      env.useBrowserCache = true;
      if (env.backends?.onnx?.wasm) {
        env.backends.onnx.wasm.wasmPaths = '/assets/vendor/transformers/';
        env.backends.onnx.wasm.numThreads = 1;
      }

      updateEngineCardStatus('ai-modnet', 5);
      onProgress?.(10, t('جارٍ تهيئة محرك البورتريه...', 'Initializing portrait engine...'));

      const processor = await AutoProcessor.from_pretrained('Xenova/modnet');
      const model = await AutoModel.from_pretrained('Xenova/modnet', {
        progress_callback: (p) => {
          if (p.status === 'progress' && Number.isFinite(p.progress)) {
            const pct = Math.round(p.progress);
            modnetProgress = pct;
            updateEngineCardStatus('ai-modnet', pct);
            onProgress?.(Math.round(15 + pct * 0.45), t(`جارٍ تنزيل نموذج البورتريه... ${pct}%`, `Downloading portrait model... ${pct}%`));
          }
        }
      });

      modnetProgress = 100;
      updateEngineCardStatus('ai-modnet', 100);
      return { model, processor };
    } catch (err) {
      modnetPipelinePromise = null;
      modnetProgress = 0;
      updateEngineCardStatus('ai-modnet', -1, err.message);
      throw err;
    }
  })();

  return modnetPipelinePromise;
}

async function runInThreadRmbg(bitmap, report) {
  const { model, processor } = await getRmbgPipeline((p, lbl) => {
    report?.(Math.round(10 + p * 0.45), lbl);
  });
  const { RawImage } = await import('@xenova/transformers');

  const origW = bitmap.width;
  const origH = bitmap.height;

  report?.(55, t('جارٍ تهيئة أبعاد الصورة بالذكاء الاصطناعي...', 'Preparing image for AI...'));
  await yieldThread(40);

  const inCanvas = document.createElement('canvas');
  inCanvas.width = 1024;
  inCanvas.height = 1024;
  const inCtx = inCanvas.getContext('2d');
  inCtx.drawImage(bitmap, 0, 0, 1024, 1024);

  const imgData = inCtx.getImageData(0, 0, 1024, 1024);
  const raw = new RawImage(imgData.data, 1024, 1024, 4);

  report?.(68, t('جارٍ عزل الخلفية بالذكاء الاصطناعي...', 'Segmenting background with AI...'));
  await yieldThread(40);

  const { pixel_values } = await processor(raw);
  report?.(80, t('جارٍ استنتاج حواف وتفاصيل الصورة...', 'Inferring neural subject boundaries...'));
  await yieldThread(40);

  const { output } = await model({ input: pixel_values });

  report?.(90, t('جارٍ تركيب القناع فائق الدقة...', 'Compositing high-res mask...'));
  await yieldThread(30);

  const mask1024 = document.createElement('canvas');
  mask1024.width = 1024;
  mask1024.height = 1024;
  const m1024Ctx = mask1024.getContext('2d');
  const mImgData = m1024Ctx.createImageData(1024, 1024);
  const mData = mImgData.data;
  const pred = output.data;

  for (let i = 0, j = 0; i < pred.length; i++, j += 4) {
    const val = Math.max(0, Math.min(255, Math.round(pred[i] * 255)));
    mData[j] = 255;
    mData[j + 1] = 255;
    mData[j + 2] = 255;
    mData[j + 3] = val;
  }
  m1024Ctx.putImageData(mImgData, 0, 0);

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = origW;
  maskCanvas.height = origH;
  const mctx = maskCanvas.getContext('2d');
  mctx.drawImage(mask1024, 0, 0, origW, origH);
  return maskCanvas;
}

async function runInThreadModnet(bitmap, report) {
  const { model, processor } = await getModnetPipeline((p, lbl) => {
    report?.(Math.round(10 + p * 0.45), lbl);
  });
  const { RawImage } = await import('@xenova/transformers');

  const origW = bitmap.width;
  const origH = bitmap.height;

  report?.(55, t('جارٍ تهيئة البورتريه بالذكاء الاصطناعي...', 'Preparing portrait for AI...'));
  await yieldThread(35);

  const inCanvas = document.createElement('canvas');
  inCanvas.width = 512;
  inCanvas.height = 512;
  const inCtx = inCanvas.getContext('2d');
  inCtx.drawImage(bitmap, 0, 0, 512, 512);

  const imgData = inCtx.getImageData(0, 0, 512, 512);
  const raw = new RawImage(imgData.data, 512, 512, 4);

  report?.(68, t('جارٍ عزل البورتريه بالذكاء الاصطناعي...', 'Segmenting portrait with AI...'));
  await yieldThread(35);

  const { pixel_values } = await processor(raw);
  report?.(82, t('جارٍ استنتاج ملامح وتفاصيل البورتريه...', 'Inferring portrait boundaries...'));
  await yieldThread(35);

  const { output } = await model({ input: pixel_values });

  report?.(90, t('جارٍ تنعيم حواف البورتريه...', 'Refining portrait edges...'));
  await yieldThread(25);

  const mask512 = document.createElement('canvas');
  mask512.width = 512;
  mask512.height = 512;
  const m512Ctx = mask512.getContext('2d');
  const mImgData = m512Ctx.createImageData(512, 512);
  const mData = mImgData.data;
  const pred = output.data;

  for (let i = 0, j = 0; i < pred.length; i++, j += 4) {
    const val = Math.max(0, Math.min(255, Math.round(pred[i] * 255)));
    mData[j] = 255;
    mData[j + 1] = 255;
    mData[j + 2] = 255;
    mData[j + 3] = val;
  }
  m512Ctx.putImageData(mImgData, 0, 0);

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = origW;
  maskCanvas.height = origH;
  const mctx = maskCanvas.getContext('2d');
  mctx.drawImage(mask512, 0, 0, origW, origH);
  return maskCanvas;
}

async function runRmbgSegmentation(bitmap, report) {
  const origW = bitmap.width;
  const origH = bitmap.height;
  const worker = getBgWorker();

  if (worker) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = origW;
    tempCanvas.height = origH;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
    tempCtx.drawImage(bitmap, 0, 0);
    const imgData = tempCtx.getImageData(0, 0, origW, origH);
    const buffer = imgData.data.buffer;

    const jobId = ++currentJobId;
    return new Promise((resolve, reject) => {
      workerPromises.set(jobId, {
        onProgress: (p, label) => report?.(p, label),
        resolve: ({ maskBuffer, width, height }) => {
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = width;
          maskCanvas.height = height;
          const mctx = maskCanvas.getContext('2d');
          const mImgData = new ImageData(new Uint8ClampedArray(maskBuffer), width, height);
          mctx.putImageData(mImgData, 0, 0);
          resolve(maskCanvas);
        },
        reject: (err) => reject(err)
      });

      worker.postMessage({
        type: 'SEGMENT',
        id: jobId,
        engine: 'ai-rmbg',
        imageBuffer: buffer,
        width: origW,
        height: origH
      }, [buffer]);
    });
  }

  return await runInThreadRmbg(bitmap, report);
}

async function runModnetSegmentation(bitmap, report) {
  const origW = bitmap.width;
  const origH = bitmap.height;
  const worker = getBgWorker();

  if (worker) {
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = origW;
    tempCanvas.height = origH;
    const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
    tempCtx.drawImage(bitmap, 0, 0);
    const imgData = tempCtx.getImageData(0, 0, origW, origH);
    const buffer = imgData.data.buffer;

    const jobId = ++currentJobId;
    return new Promise((resolve, reject) => {
      workerPromises.set(jobId, {
        onProgress: (p, label) => report?.(p, label),
        resolve: ({ maskBuffer, width, height }) => {
          const maskCanvas = document.createElement('canvas');
          maskCanvas.width = width;
          maskCanvas.height = height;
          const mctx = maskCanvas.getContext('2d');
          const mImgData = new ImageData(new Uint8ClampedArray(maskBuffer), width, height);
          mctx.putImageData(mImgData, 0, 0);
          resolve(maskCanvas);
        },
        reject: (err) => reject(err)
      });

      worker.postMessage({
        type: 'SEGMENT',
        id: jobId,
        engine: 'ai-modnet',
        imageBuffer: buffer,
        width: origW,
        height: origH
      }, [buffer]);
    });
  }

  return await runInThreadModnet(bitmap, report);
}

// ============================================================================
// 3. State-of-the-Art Instant Edge-Connected Palette & Gradient Matting
// (Pure JS Computer Vision, Zero Network, High Performance, Non-AI)
// ============================================================================
function generateAdaptiveMask(bitmap, settings) {
  const origW = bitmap.width;
  const origH = bitmap.height;

  // Process at balanced grid resolution (max 800px) for sub-20ms speed and silky smooth anti-aliased output
  const maxDim = 800;
  const scale = Math.min(1.0, maxDim / Math.max(origW, origH));
  const w = Math.max(48, Math.round(origW * scale));
  const h = Math.max(48, Math.round(origH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(bitmap, 0, 0, w, h);

  const imgData = ctx.getImageData(0, 0, w, h);
  const src = imgData.data;
  const totalPixels = w * h;

  // 1. Luminance & 3x3 Sobel Gradient Energy Map (Detects physical subject boundaries)
  const lum = new Float32Array(totalPixels);
  for (let i = 0, p = 0; i < src.length; i += 4, p++) {
    lum[p] = 0.299 * src[i] + 0.587 * src[i + 1] + 0.114 * src[i + 2];
  }

  const edges = new Float32Array(totalPixels);
  for (let y = 1; y < h - 1; y++) {
    const yPrev = (y - 1) * w;
    const yCurr = y * w;
    const yNext = (y + 1) * w;

    for (let x = 1; x < w - 1; x++) {
      const gx =
        -lum[yPrev + (x - 1)] + lum[yPrev + (x + 1)]
        - 2 * lum[yCurr + (x - 1)] + 2 * lum[yCurr + (x + 1)]
        - lum[yNext + (x - 1)] + lum[yNext + (x + 1)];

      const gy =
        -lum[yPrev + (x - 1)] - 2 * lum[yPrev + x] - lum[yPrev + (x + 1)]
        + lum[yNext + (x - 1)] + 2 * lum[yNext + x] + lum[yNext + (x + 1)];

      edges[yCurr + x] = Math.sqrt(gx * gx + gy * gy);
    }
  }

  // 2. Multi-point Perimeter Sampling (Sample all 4 borders at depths 0 to 2)
  const borderSamples = [];
  const samplePixel = (px, py) => {
    const idx = (py * w + px) * 4;
    borderSamples.push([src[idx], src[idx + 1], src[idx + 2]]);
  };

  const stepX = Math.max(1, Math.floor(w / 50));
  const stepY = Math.max(1, Math.floor(h / 50));

  for (let d = 0; d < 2; d++) {
    for (let x = 0; x < w; x += stepX) {
      samplePixel(x, d);
      samplePixel(x, Math.max(0, h - 1 - d));
    }
    for (let y = 0; y < h; y += stepY) {
      samplePixel(d, y);
      samplePixel(Math.max(0, w - 1 - d), y);
    }
  }

  // Redmean perceptual color distance (Accurate Delta-E approximation in sRGB)
  const colorDist = (r1, g1, b1, r2, g2, b2) => {
    const dr = r1 - r2;
    const dg = g1 - g2;
    const db = b1 - b2;
    const rMean = (r1 + r2) / 2;
    return Math.sqrt(
      (2 + rMean / 256) * dr * dr +
      4 * dg * dg +
      (2 + (255 - rMean) / 256) * db * db
    );
  };

  // 3. Cluster perimeter samples into dominant background color clusters (Adaptive Palette)
  const clusters = [];
  const maxClusters = 5;
  const clusterDistThreshold = 26;

  for (const [r, g, b] of borderSamples) {
    let matched = false;
    for (const c of clusters) {
      if (colorDist(r, g, b, c.r, c.g, c.b) < clusterDistThreshold) {
        c.r = (c.r * c.count + r) / (c.count + 1);
        c.g = (c.g * c.count + g) / (c.count + 1);
        c.b = (c.b * c.count + b) / (c.count + 1);
        c.count++;
        matched = true;
        break;
      }
    }
    if (!matched && clusters.length < maxClusters) {
      clusters.push({ r, g, b, count: 1 });
    }
  }

  clusters.sort((a, b) => b.count - a.count);

  // 4. Sensitivity Tolerance
  const userTol = Number(settings.wandTolerance || 22);
  const baseTol = Math.max(14, userTol * 1.85);
  const edgeCutoff = Math.max(28, 115 - userTol);

  const isBg = new Uint8Array(totalPixels);
  const queue = new Int32Array(totalPixels * 2);
  let qHead = 0;
  let qTail = 0;

  const minPaletteDist = (r, g, b) => {
    let minD = 9999;
    for (const c of clusters) {
      const d = colorDist(r, g, b, c.r, c.g, c.b);
      if (d < minD) minD = d;
    }
    return minD;
  };

  // Seed perimeter pixels matching any background cluster
  const trySeed = (x, y) => {
    const pIdx = y * w + x;
    if (isBg[pIdx]) return;
    const sIdx = pIdx * 4;
    const d = minPaletteDist(src[sIdx], src[sIdx + 1], src[sIdx + 2]);
    if (d <= baseTol * 1.35) {
      isBg[pIdx] = 1;
      queue[qTail++] = x;
      queue[qTail++] = y;
    }
  };

  for (let x = 0; x < w; x++) {
    trySeed(x, 0);
    trySeed(x, h - 1);
  }
  for (let y = 0; y < h; y++) {
    trySeed(0, y);
    trySeed(w - 1, y);
  }

  // Saliency Box (Center protection against edge leaks)
  const cxMin = Math.floor(w * 0.22);
  const cxMax = Math.floor(w * 0.78);
  const cyMin = Math.floor(h * 0.20);
  const cyMax = Math.floor(h * 0.82);

  // 5. Multi-Criteria Flood Fill with Sobel Edge Barriers
  while (qHead < qTail) {
    const cx = queue[qHead++];
    const cy = queue[qHead++];
    const cIdx = cy * w + cx;
    const csIdx = cIdx * 4;
    const cr = src[csIdx], cg = src[csIdx + 1], cb = src[csIdx + 2];

    const neighbors = [
      cx + 1, cy,
      cx - 1, cy,
      cx, cy + 1,
      cx, cy - 1
    ];

    for (let i = 0; i < 8; i += 2) {
      const nx = neighbors[i];
      const ny = neighbors[i + 1];

      if (nx >= 0 && nx < w && ny >= 0 && ny < h) {
        const nIdx = ny * w + nx;
        if (!isBg[nIdx]) {
          // If hit a strong physical edge, DO NOT cross!
          if (edges[nIdx] > edgeCutoff) continue;

          const nsIdx = nIdx * 4;
          const nr = src[nsIdx], ng = src[nsIdx + 1], nb = src[nsIdx + 2];
          const pDist = minPaletteDist(nr, ng, nb);

          const inCenter = nx >= cxMin && nx <= cxMax && ny >= cyMin && ny <= cyMax;
          const effectiveTol = inCenter ? baseTol * 0.85 : baseTol;
          const localDist = colorDist(cr, cg, cb, nr, ng, nb);

          if (pDist <= effectiveTol && localDist <= effectiveTol * 0.75) {
            isBg[nIdx] = 1;
            queue[qTail++] = nx;
            queue[qTail++] = ny;
          }
        }
      }
    }
  }

  // 6. Morphological Refinement: Fill pinholes inside foreground subject
  const refinedBg = new Uint8Array(isBg);
  for (let y = 1; y < h - 1; y++) {
    const yRow = y * w;
    for (let x = 1; x < w - 1; x++) {
      const idx = yRow + x;
      if (isBg[idx] === 1) {
        const fgCount = (
          (isBg[idx - 1] === 0 ? 1 : 0) +
          (isBg[idx + 1] === 0 ? 1 : 0) +
          (isBg[idx - w] === 0 ? 1 : 0) +
          (isBg[idx + w] === 0 ? 1 : 0)
        );
        if (fgCount >= 3) {
          refinedBg[idx] = 0;
        }
      }
    }
  }

  // 7. Sub-pixel Feathered Alpha Matte (Anti-Aliasing)
  const maskCanvasScaled = document.createElement('canvas');
  maskCanvasScaled.width = w;
  maskCanvasScaled.height = h;
  const mctxScaled = maskCanvasScaled.getContext('2d');
  const maskImgData = mctxScaled.createImageData(w, h);
  const mask = maskImgData.data;

  for (let y = 0; y < h; y++) {
    const yRow = y * w;
    for (let x = 0; x < w; x++) {
      const pIdx = yRow + x;
      const mIdx = pIdx * 4;

      if (refinedBg[pIdx] === 1) {
        mask[mIdx] = 255;
        mask[mIdx + 1] = 255;
        mask[mIdx + 2] = 255;
        mask[mIdx + 3] = 0;
      } else {
        let bgNeighborCount = 0;
        if (x > 0 && refinedBg[pIdx - 1]) bgNeighborCount++;
        if (x < w - 1 && refinedBg[pIdx + 1]) bgNeighborCount++;
        if (y > 0 && refinedBg[pIdx - w]) bgNeighborCount++;
        if (y < h - 1 && refinedBg[pIdx + w]) bgNeighborCount++;

        let alpha = 255;
        if (bgNeighborCount > 0) {
          alpha = Math.round(255 * (1 - (bgNeighborCount / 6.0)));
        }

        mask[mIdx] = 255;
        mask[mIdx + 1] = 255;
        mask[mIdx + 2] = 255;
        mask[mIdx + 3] = alpha;
      }
    }
  }

  mctxScaled.putImageData(maskImgData, 0, 0);

  // 8. Bicubic upscale to original bitmap dimensions
  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = origW;
  maskCanvas.height = origH;
  const mctx = maskCanvas.getContext('2d');
  mctx.imageSmoothingEnabled = true;
  mctx.imageSmoothingQuality = 'high';
  mctx.drawImage(maskCanvasScaled, 0, 0, origW, origH);

  return maskCanvas;
}

// ============================================================================
// 4. Chroma Key / Color Isolation Mask
// ============================================================================
function generateChromaMask(bitmap, settings) {
  const origW = bitmap.width;
  const origH = bitmap.height;

  const canvas = document.createElement('canvas');
  canvas.width = origW;
  canvas.height = origH;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0);

  const imgData = ctx.getImageData(0, 0, origW, origH);
  const src = imgData.data;

  const maskCanvas = document.createElement('canvas');
  maskCanvas.width = origW;
  maskCanvas.height = origH;
  const mctx = maskCanvas.getContext('2d');
  const maskData = mctx.createImageData(origW, origH);
  const mask = maskData.data;

  let keyR = 0, keyG = 255, keyB = 0;
  if (settings.chromaPreset === 'blue') {
    keyR = 0; keyG = 0; keyB = 255;
  } else if (settings.chromaPreset === 'auto') {
    keyR = src[0]; keyG = src[1]; keyB = src[2];
  } else if (settings.chromaColor) {
    const hex = settings.chromaColor.replace('#', '');
    keyR = parseInt(hex.substring(0, 2), 16) || 0;
    keyG = parseInt(hex.substring(2, 4), 16) || 255;
    keyB = parseInt(hex.substring(4, 6), 16) || 0;
  }

  const tol = Number(settings.wandTolerance || 25);
  const tolSq = (tol * 3.5) * (tol * 3.5);

  for (let i = 0; i < src.length; i += 4) {
    const r = src[i];
    const g = src[i + 1];
    const b = src[i + 2];
    const a = src[i + 3];

    if (a < 10) {
      mask[i + 3] = 0;
      continue;
    }

    const dr = r - keyR;
    const dg = g - keyG;
    const db = b - keyB;
    const distSq = dr * dr + dg * dg + db * db;

    mask[i] = 255;
    mask[i + 1] = 255;
    mask[i + 2] = 255;
    mask[i + 3] = distSq <= tolSq ? 0 : 255;
  }

  mctx.putImageData(maskData, 0, 0);
  return maskCanvas;
}

// ============================================================================
// Universal Mask Cache & Switcher (Preserves manual brush edits)
// ============================================================================
async function ensureItemMask(item, bitmap, settings, report) {
  const engine = settings.engine || 'ai-rmbg';
  const currentKey = `${engine}_${settings.wandTolerance || 22}_${settings.chromaPreset || ''}_${settings.chromaColor || ''}`;

  // If user has manual brush edits, and engine hasn't changed, KEEP the edited mask!
  if (item._bgMaskCanvas && item._hasBrushEdits && item._bgEngine === engine) {
    return item._bgMaskCanvas;
  }

  // If mask is already computed for these exact settings, reuse it!
  if (item._bgMaskCanvas && item._bgMaskKey === currentKey && item._bgMaskCanvas.width === bitmap.width) {
    return item._bgMaskCanvas;
  }

  // Mutex promise: if inference is already in progress for this item, share it to prevent duplicate execution!
  if (item._maskPromise) {
    return await item._maskPromise;
  }

  item._maskPromise = (async () => {
    try {
      let maskCanvas = null;

      if (engine === 'ai-rmbg') {
        try {
          maskCanvas = await runRmbgSegmentation(bitmap, report);
        } catch (err) {
          console.warn('AI RMBG failed, falling back to adaptive:', err);
          report?.(50, t('تعذر تشغيل النموذج، جارٍ التبديل للمحرك الذكي الفوري...', 'Model failed, falling back to instant smart engine...'));
          maskCanvas = generateAdaptiveMask(bitmap, settings);
        }
      } else if (engine === 'ai-modnet') {
        try {
          maskCanvas = await runModnetSegmentation(bitmap, report);
        } catch (err) {
          console.warn('AI MODNet failed, falling back to adaptive:', err);
          report?.(50, t('تعذر تشغيل النموذج، جارٍ التبديل للمحرك الذكي الفوري...', 'Model failed, falling back to instant smart engine...'));
          maskCanvas = generateAdaptiveMask(bitmap, settings);
        }
      } else if (engine === 'chroma') {
        maskCanvas = generateChromaMask(bitmap, settings);
      } else {
        maskCanvas = generateAdaptiveMask(bitmap, settings);
      }

      item._bgMaskCanvas = maskCanvas;

      // Create pristine backup of base AI mask for "Reset" feature
      item._bgBaseMaskCanvas = document.createElement('canvas');
      item._bgBaseMaskCanvas.width = maskCanvas.width;
      item._bgBaseMaskCanvas.height = maskCanvas.height;
      item._bgBaseMaskCanvas.getContext('2d').drawImage(maskCanvas, 0, 0);

      item._bgMaskKey = currentKey;
      item._bgEngine = engine;
      item._hasBrushEdits = false;
      return maskCanvas;
    } finally {
      item._maskPromise = null;
    }
  })();

  return await item._maskPromise;
}

// ============================================================================
// Fast display rendering helper
// ============================================================================
let fastCompCanvas = null;

function fastRenderDisplay(displayCanvas, bitmap, maskCanvas, settings) {
  const dctx = displayCanvas.getContext('2d');
  const w = displayCanvas.width;
  const h = displayCanvas.height;

  dctx.clearRect(0, 0, w, h);

  if (settings.bgPreview === 'white') {
    dctx.fillStyle = '#ffffff';
    dctx.fillRect(0, 0, w, h);
  } else if (settings.bgPreview === 'black') {
    dctx.fillStyle = '#000000';
    dctx.fillRect(0, 0, w, h);
  } else if (settings.bgPreview === 'custom') {
    dctx.fillStyle = settings.bgColor || '#3b82f6';
    dctx.fillRect(0, 0, w, h);
  } else {
    drawCheckerboard(dctx, w, h);
  }

  if (!fastCompCanvas) {
    fastCompCanvas = document.createElement('canvas');
  }
  if (fastCompCanvas.width !== w || fastCompCanvas.height !== h) {
    fastCompCanvas.width = w;
    fastCompCanvas.height = h;
  }
  const cctx = fastCompCanvas.getContext('2d');
  cctx.save();
  cctx.globalCompositeOperation = 'source-over';
  cctx.clearRect(0, 0, w, h);
  cctx.drawImage(bitmap, 0, 0, w, h);
  cctx.globalCompositeOperation = 'destination-in';
  cctx.drawImage(maskCanvas, 0, 0, w, h);
  cctx.restore();

  dctx.drawImage(fastCompCanvas, 0, 0);
}

// ============================================================================
// Configuration Fields & Interactive Toolbar
// ============================================================================
const fields = [
  {
    key: 'engine',
    label: t('محرك إزالة الخلفية', 'Background Removal Engine'),
    type: 'cards',
    span: 'full',
    default: 'ai-rmbg',
    disclaimer: t(
      '<strong>معالجة محلية كاملة وخصوصية مطلقة 100%:</strong> تعمل جميع محركات إزالة الخلفية والذكاء الاصطناعي مباشرة داخل متصفح جهازك دون رفع أي صورة إلى خوادم خارجية. يتطلب نموذج الذكاء الاصطناعي تحميلاً لمرة واحدة فقط في ذاكرة متصفحك، ليمنحك بعد ذلك تحكماً كاملاً، معالجة غير محدودة وبدون أي قيود أو انتظار.',
      '<strong>100% Private On-Device Processing:</strong> All background removal engines and AI models run entirely inside your local browser without uploading photos to external servers. The neural model loads once into your device cache, providing complete control, unlimited background removals, and zero server queues.'
    ),
    options: [
      {
        value: 'ai-rmbg',
        title: t('الأقوى (ذكاء اصطناعي)', 'The Strongest (AI)'),
        badge: t('الأفضل للمنتجات والمشاهد', 'Best for Products & Scenes'),
        desc: t('دقة عصبية فائقة للعزل المعقد وتفاصيل الشعر والحواف الصعبة (معالجة هادئة في الخلفية)', 'Maximum AI precision for complex edges and fine hair with isolated background execution'),
        statusId: 'engine-status-ai-rmbg',
        progressId: 'engine-progress-ai-rmbg',
        statusText: t('جارٍ الفحص...', 'Checking...'),
        initialState: 'idle',
        iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
          <circle cx="12" cy="12" r="4"/>
        </svg>`
      },
      {
        value: 'ai-modnet',
        title: t('فائق السرعة (بورتريه)', 'Ultra Fast (Portrait)'),
        badge: t('الأفضل للأشخاص والبورتريه', 'Best for People & Portraits'),
        desc: t('عزل سريع وخفيف مصمم خصيصاً للوجوه والأشخاص', 'Fast, lightweight neural portrait matting'),
        statusId: 'engine-status-ai-modnet',
        progressId: 'engine-progress-ai-modnet',
        statusText: t('تجهيز فوري عند الاختيار', 'Loads on click'),
        initialState: 'idle',
        iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M19 8l3 3-3 3M22 11h-7"/>
        </svg>`
      },
      {
        value: 'adaptive',
        title: t('الفوري المطوّر', 'Enhanced Instant'),
        badge: t('بدون إنترنت · فائق الدقة', 'Offline · Smart Matting'),
        desc: t('خوارزمية بصرية فورية متقدمة لعزل حواف الكائنات والخلفيات المتعددة فورياً بدون استهلاك باقة الإنترنت', 'Advanced instant edge-connected palette & gradient matting, zero download'),
        statusId: 'engine-status-adaptive',
        statusText: t('يعمل فوراً ⚡', 'Instant ⚡'),
        initialState: 'ready',
        iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
        </svg>`
      },
      {
        value: 'chroma',
        title: t('عزل الكروما', 'Chroma Key'),
        badge: t('شاشة خضراء وزرقاء', 'Green & Blue Screen'),
        desc: t('عزل استوديو فوري للخلفيات الموحدة والخضراء', 'Studio keying for solid green/blue backdrops'),
        statusId: 'engine-status-chroma',
        statusText: t('يعمل فوراً 🎯', 'Instant 🎯'),
        initialState: 'ready',
        iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2"/>
          <path d="M8 21h8M12 17v4"/>
          <circle cx="12" cy="10" r="3"/>
        </svg>`
      }
    ]
  },
  {
    key: 'chromaPreset',
    label: t('نمط لون الكروما', 'Chroma Key Color'),
    type: 'segmented',
    span: 'full',
    default: 'auto',
    showIf: (s) => s.engine === 'chroma',
    options: [
      { value: 'auto', label: t('تلقائي من زاوية الصورة 🎯', 'Auto Sample Corner 🎯') },
      { value: 'green', label: t('شاشة خضراء (Green Screen)', 'Green Screen') },
      { value: 'blue', label: t('شاشة زرقاء (Blue Screen)', 'Blue Screen') },
      { value: 'custom', label: t('لون محدد', 'Custom Color') }
    ]
  },
  {
    key: 'chromaColor',
    label: t('اختر لون الكروما المطلوب عزله', 'Pick Chroma Color'),
    type: 'color',
    default: '#00ff00',
    showIf: (s) => s.engine === 'chroma' && s.chromaPreset === 'custom'
  },
  {
    key: 'bgPreview',
    label: t('خلفية المعاينة والتصدير', 'Preview & Export Background'),
    type: 'segmented',
    span: 'full',
    default: 'transparent',
    options: [
      { value: 'transparent', label: t('شفافة PNG (مربعات)', 'Transparent PNG') },
      { value: 'white', label: t('خلفية بيضاء', 'White Background') },
      { value: 'black', label: t('خلفية سوداء', 'Black Background') },
      { value: 'custom', label: t('لون مخصص', 'Custom Color') }
    ]
  },
  {
    key: 'bgColor',
    label: t('اللون المخصص للخلفية', 'Custom Background Color'),
    type: 'color',
    default: '#3b82f6',
    showIf: (s) => s.bgPreview === 'custom'
  },
  // Interactive Tools Bar (Rendered on Stage Toolbar Directly Above Canvas)
  {
    key: 'brushMode',
    label: t('أدوات المحرر', 'Editor Tools'),
    type: 'segmented',
    placement: 'stage-toolbar',
    default: 'erase',
    options: [
      { value: 'erase', label: t('فرشاة المسح ⌫', 'Erase Brush ⌫') },
      { value: 'restore', label: t('فرشاة الاستعادة ↺', 'Restore Brush ↺') },
      { value: 'smooth', label: t('تنعيم الحواف ✨', 'Smooth Edge ✨') },
      { value: 'wand', label: t('العصا السحرية 🪄', 'Magic Wand 🪄') }
    ]
  },
  {
    key: 'brushSize',
    label: t('حجم الفرشاة', 'Brush Size'),
    type: 'range',
    placement: 'stage-toolbar',
    min: 5,
    max: 120,
    step: 5,
    default: 30,
    suffix: 'px',
    showIf: (s) => s.brushMode !== 'wand'
  },
  {
    key: 'wandTolerance',
    label: t('حساسية العصا والعزل %', 'Wand / Keying Sensitivity %'),
    type: 'range',
    placement: 'stage-toolbar',
    min: 1,
    max: 70,
    step: 1,
    default: 22,
    suffix: '%'
  }
];

const root = document.getElementById('bg-remover-app');
if (root) {
  mountImageBatch({
    rootId: 'bg-remover-app',
    fields,
    zipName: 'transparent-images.zip',
    dropTitle: t('اسحب الصور هنا أو انقر لاختيارها لإزالة الخلفية', 'Drop images here or click to choose for background removal'),
    dropSubtitle: t('دعم الدفعات المتعددة · تفريغ فائق الدقة بالذكاء الاصطناعي بدون رفع لخوادم خارجية', 'Batch support · Instant local AI background removal with zero uploads'),
    previewTitle: t('المحرر', 'Editor'),
    previewSubtitle: t('شريط الأدوات التفاعلي والمعاينة المباشرة', 'Interactive tools & live result'),
    renderPreview: async ({ canvas, bitmap, settings, item, statsEl, showOverlay, hideOverlay }) => {
      const origW = bitmap.width;
      const origH = bitmap.height;

      const containerW = canvas.parentElement?.clientWidth || 720;
      const maxStageW = Math.min(760, containerW - 24);
      const maxStageH = 460;
      const scale = Math.min(maxStageW / origW, maxStageH / origH, 1.0);
      const displayW = Math.max(64, Math.round(origW * scale));
      const displayH = Math.max(48, Math.round(origH * scale));

      canvas.width = displayW;
      canvas.height = displayH;
      canvas.style.width = `${displayW}px`;
      canvas.style.height = `${displayH}px`;

      const engine = settings.engine || 'ai-rmbg';
      const currentKey = `${engine}_${settings.wandTolerance || 22}_${settings.chromaPreset || ''}_${settings.chromaColor || ''}`;

      // Only compute base mask if missing or if settings changed without manual touchups
      const engineChanged = item._bgEngine && item._bgEngine !== engine;
      const needsCompute = !item._bgMaskCanvas || engineChanged || (!item._hasBrushEdits && item._bgMaskKey !== currentKey);

      if (needsCompute) {
        const engTitle = engine === 'ai-modnet'
          ? t('معالجة عصبية بالذكاء الاصطناعي للبورتريه...', 'Neural AI Portrait Processing...')
          : engine === 'adaptive'
          ? t('معالجة فورية ذكية متصلة بالحواف (بدون إنترنت)...', 'Instant Smart Edge Matting (Zero Download)...')
          : engine === 'chroma'
          ? t('عزل الكروما واللون المختار...', 'Chroma Key & color isolation...')
          : t('معالجة عصبية بالذكاء الاصطناعي الأقوى...', 'Neural AI Processing...');

        showOverlay?.(
          t('جارٍ إزالة الخلفية ومعالجة الصورة...', 'Removing background & processing...'),
          engTitle,
          10
        );
      }

      let maskCanvas;
      try {
        maskCanvas = await ensureItemMask(item, bitmap, settings, (p, label) => {
          showOverlay?.(
            label || t('جارٍ المعالجة...', 'Processing...'),
            null,
            p
          );
        });
      } finally {
        if (needsCompute) {
          hideOverlay?.();
        }
      }

      // Fast GPU composite onto display canvas
      fastRenderDisplay(canvas, bitmap, maskCanvas, settings);

      // Stats badge
      if (statsEl) {
        const engName = engine === 'ai-rmbg'
          ? t('الذكاء الاصطناعي (الأقوى)', 'AI (The Strongest)')
          : engine === 'ai-modnet'
          ? t('الذكاء الاصطناعي (فائق السرعة)', 'AI (Ultra Fast)')
          : engine === 'chroma'
          ? t('عزل الكروما', 'Chroma Key')
          : t('الفوري (بدون إنترنت)', 'Instant (Offline)');
        statsEl.textContent = `${origW} × ${origH} px · ${engName} · PNG`;
      }
    },
    onPreviewPointer: ({ stageWrap, canvasEl, stageToolbar, getActiveItem, getBitmap, getSettings, drawPreview }) => {
      let isDrawing = false;
      let lastPt = null;

      // Ensure dedicated transparent cursor overlay canvas inside stageWrap
      let cursorOverlay = stageWrap.querySelector('.batch-cursor-overlay');
      if (!cursorOverlay) {
        cursorOverlay = document.createElement('canvas');
        cursorOverlay.className = 'batch-cursor-overlay';
        stageWrap.appendChild(cursorOverlay);
      }

      // Add Undo / Redo / Reset toolbar group
      let historyGroup = stageToolbar?.querySelector('.batch-stage-history-group');
      let undoBtn, redoBtn, resetBtn;

      if (stageToolbar && !historyGroup) {
        historyGroup = document.createElement('div');
        historyGroup.className = 'batch-stage-history-group';

        undoBtn = document.createElement('button');
        undoBtn.type = 'button';
        undoBtn.className = 'batch-stage-undo-btn';
        undoBtn.title = t('تراجع (Ctrl+Z)', 'Undo (Ctrl+Z)');
        undoBtn.setAttribute('aria-label', 'Undo');
        undoBtn.disabled = true;
        undoBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="9 14 4 9 9 4"></polyline>
            <path d="M20 20v-7a4 4 0 0 0-4-4H4"></path>
          </svg>
          <span>${t('تراجع', 'Undo')}</span>
        `;

        redoBtn = document.createElement('button');
        redoBtn.type = 'button';
        redoBtn.className = 'batch-stage-redo-btn';
        redoBtn.title = t('إعادة (Ctrl+Y)', 'Redo (Ctrl+Y)');
        redoBtn.setAttribute('aria-label', 'Redo');
        redoBtn.disabled = true;
        redoBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="15 14 20 9 15 4"></polyline>
            <path d="M4 20v-7a4 4 0 0 1 4-4h12"></path>
          </svg>
          <span>${t('إعادة', 'Redo')}</span>
        `;

        resetBtn = document.createElement('button');
        resetBtn.type = 'button';
        resetBtn.className = 'batch-brush-reset-btn';
        resetBtn.title = t('استعادة القناع الأصلي التلقائي بالذكاء الاصطناعي', 'Reset to AI Mask');
        resetBtn.setAttribute('aria-label', 'Reset to AI Mask');
        resetBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
            <path d="M3 3v5h5"/>
          </svg>
          <span>${t('استعادة الأصلي', 'Reset')}</span>
        `;

        historyGroup.append(undoBtn, redoBtn, resetBtn);
        stageToolbar.appendChild(historyGroup);
      } else if (stageToolbar) {
        undoBtn = historyGroup.querySelector('.batch-stage-undo-btn');
        redoBtn = historyGroup.querySelector('.batch-stage-redo-btn');
        resetBtn = historyGroup.querySelector('.batch-brush-reset-btn');
      }

      const updateUndoRedoUI = () => {
        const item = getActiveItem();
        if (undoBtn) undoBtn.disabled = !item?._undoStack?.length;
        if (redoBtn) redoBtn.disabled = !item?._redoStack?.length;
      };

      const saveUndoSnapshot = (item) => {
        if (!item?._bgMaskCanvas) return;
        if (!item._undoStack) item._undoStack = [];
        if (!item._redoStack) item._redoStack = [];

        const copy = document.createElement('canvas');
        copy.width = item._bgMaskCanvas.width;
        copy.height = item._bgMaskCanvas.height;
        copy.getContext('2d').drawImage(item._bgMaskCanvas, 0, 0);

        item._undoStack.push(copy);
        if (item._undoStack.length > 20) {
          item._undoStack.shift();
        }
        item._redoStack = [];
        updateUndoRedoUI();
      };

      const performUndo = () => {
        const item = getActiveItem();
        if (!item?._undoStack?.length || !item?._bgMaskCanvas) return;

        const currentCopy = document.createElement('canvas');
        currentCopy.width = item._bgMaskCanvas.width;
        currentCopy.height = item._bgMaskCanvas.height;
        currentCopy.getContext('2d').drawImage(item._bgMaskCanvas, 0, 0);
        if (!item._redoStack) item._redoStack = [];
        item._redoStack.push(currentCopy);

        const prevState = item._undoStack.pop();
        const mctx = item._bgMaskCanvas.getContext('2d');
        mctx.clearRect(0, 0, item._bgMaskCanvas.width, item._bgMaskCanvas.height);
        mctx.drawImage(prevState, 0, 0);
        item._hasBrushEdits = true;

        const bitmap = getBitmap();
        if (bitmap) {
          fastRenderDisplay(canvasEl, bitmap, item._bgMaskCanvas, getSettings());
        }
        updateUndoRedoUI();
      };

      const performRedo = () => {
        const item = getActiveItem();
        if (!item?._redoStack?.length || !item?._bgMaskCanvas) return;

        const currentCopy = document.createElement('canvas');
        currentCopy.width = item._bgMaskCanvas.width;
        currentCopy.height = item._bgMaskCanvas.height;
        currentCopy.getContext('2d').drawImage(item._bgMaskCanvas, 0, 0);
        if (!item._undoStack) item._undoStack = [];
        item._undoStack.push(currentCopy);

        const nextState = item._redoStack.pop();
        const mctx = item._bgMaskCanvas.getContext('2d');
        mctx.clearRect(0, 0, item._bgMaskCanvas.width, item._bgMaskCanvas.height);
        mctx.drawImage(nextState, 0, 0);
        item._hasBrushEdits = true;

        const bitmap = getBitmap();
        if (bitmap) {
          fastRenderDisplay(canvasEl, bitmap, item._bgMaskCanvas, getSettings());
        }
        updateUndoRedoUI();
      };

      undoBtn?.addEventListener('click', performUndo);
      redoBtn?.addEventListener('click', performRedo);

      resetBtn?.addEventListener('click', () => {
        const activeItem = getActiveItem();
        if (activeItem?._bgBaseMaskCanvas && activeItem?._bgMaskCanvas) {
          saveUndoSnapshot(activeItem);
          const mctx = activeItem._bgMaskCanvas.getContext('2d');
          mctx.clearRect(0, 0, activeItem._bgMaskCanvas.width, activeItem._bgMaskCanvas.height);
          mctx.drawImage(activeItem._bgBaseMaskCanvas, 0, 0);
          activeItem._hasBrushEdits = false;
          const bitmap = getBitmap();
          if (bitmap) {
            fastRenderDisplay(canvasEl, bitmap, activeItem._bgMaskCanvas, getSettings());
          }
          updateUndoRedoUI();
        }
      });

      // Global Keyboard Shortcut handler for Ctrl+Z and Ctrl+Y / Ctrl+Shift+Z
      const onKeyDown = (e) => {
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || document.activeElement?.isContentEditable) return;

        const isCtrlOrCmd = e.ctrlKey || e.metaKey;
        if (!isCtrlOrCmd) return;

        if (e.key.toLowerCase() === 'z' && !e.shiftKey) {
          e.preventDefault();
          performUndo();
        } else if ((e.key.toLowerCase() === 'y') || (e.shiftKey && e.key.toLowerCase() === 'z')) {
          e.preventDefault();
          performRedo();
        }
      };
      window.addEventListener('keydown', onKeyDown);

      // Align cursorOverlay exactly over canvasEl
      const alignCursorOverlay = () => {
        const cRect = canvasEl.getBoundingClientRect();
        const sRect = stageWrap.getBoundingClientRect();
        const left = Math.round(cRect.left - sRect.left);
        const top = Math.round(cRect.top - sRect.top);
        cursorOverlay.style.position = 'absolute';
        cursorOverlay.style.left = `${left}px`;
        cursorOverlay.style.top = `${top}px`;
        cursorOverlay.style.width = `${Math.round(cRect.width)}px`;
        cursorOverlay.style.height = `${Math.round(cRect.height)}px`;
        cursorOverlay.style.zIndex = '20';
        cursorOverlay.style.pointerEvents = 'none';

        if (cursorOverlay.width !== canvasEl.width || cursorOverlay.height !== canvasEl.height) {
          cursorOverlay.width = canvasEl.width;
          cursorOverlay.height = canvasEl.height;
        }
      };

      const drawCursorRing = (x, y, radius, mode) => {
        alignCursorOverlay();
        const octx = cursorOverlay.getContext('2d');
        octx.clearRect(0, 0, cursorOverlay.width, cursorOverlay.height);

        if (mode === 'wand') {
          octx.save();
          octx.fillStyle = '#f59e0b';
          octx.beginPath();
          octx.arc(x, y, 5, 0, Math.PI * 2);
          octx.fill();
          octx.strokeStyle = '#ffffff';
          octx.lineWidth = 1.5;
          octx.stroke();
          octx.restore();
          return;
        }

        octx.save();
        octx.strokeStyle = mode === 'erase' ? '#ef4444' : mode === 'restore' ? '#10b981' : '#38bdf8';
        octx.lineWidth = 2;
        octx.setLineDash([4, 3]);
        octx.beginPath();
        octx.arc(x, y, Math.max(4, radius), 0, Math.PI * 2);
        octx.stroke();
        octx.restore();
      };

      const clearCursorRing = () => {
        const octx = cursorOverlay.getContext('2d');
        octx.clearRect(0, 0, cursorOverlay.width, cursorOverlay.height);
      };

      const getPoints = (e) => {
        const rect = canvasEl.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        const activeItem = getActiveItem();
        if (!activeItem?._bgMaskCanvas) return null;

        const maskCanvas = activeItem._bgMaskCanvas;
        const clampedClientX = Math.max(rect.left, Math.min(rect.right, e.clientX));
        const clampedClientY = Math.max(rect.top, Math.min(rect.bottom, e.clientY));

        const relX = clampedClientX - rect.left;
        const relY = clampedClientY - rect.top;

        return {
          maskX: relX * (maskCanvas.width / rect.width),
          maskY: relY * (maskCanvas.height / rect.height),
          dispX: relX * (canvasEl.width / rect.width),
          dispY: relY * (canvasEl.height / rect.height),
          scaleRatio: maskCanvas.width / canvasEl.width,
          isInside: e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom
        };
      };

      const applyBrushStroke = (currPt) => {
        if (!currPt) return;
        const activeItem = getActiveItem();
        if (!activeItem?._bgMaskCanvas) return;

        const maskCanvas = activeItem._bgMaskCanvas;
        const mctx = maskCanvas.getContext('2d');
        const settings = getSettings();
        const mode = settings.brushMode || 'erase';
        const bRadius = (Number(settings.brushSize) || 30) * (currPt.scaleRatio / 2);

        mctx.save();
        if (mode === 'erase') {
          mctx.globalCompositeOperation = 'destination-out';
          mctx.lineWidth = bRadius * 2;
          mctx.lineCap = 'round';
          mctx.lineJoin = 'round';
          mctx.beginPath();
          if (lastPt) {
            mctx.moveTo(lastPt.maskX, lastPt.maskY);
            mctx.lineTo(currPt.maskX, currPt.maskY);
            mctx.stroke();
          } else {
            mctx.arc(currPt.maskX, currPt.maskY, bRadius, 0, Math.PI * 2);
            mctx.fill();
          }
        } else if (mode === 'restore') {
          mctx.globalCompositeOperation = 'source-over';
          mctx.fillStyle = '#ffffff';
          mctx.strokeStyle = '#ffffff';
          mctx.lineWidth = bRadius * 2;
          mctx.lineCap = 'round';
          mctx.lineJoin = 'round';
          mctx.beginPath();
          if (lastPt) {
            mctx.moveTo(lastPt.maskX, lastPt.maskY);
            mctx.lineTo(currPt.maskX, currPt.maskY);
            mctx.stroke();
          } else {
            mctx.arc(currPt.maskX, currPt.maskY, bRadius, 0, Math.PI * 2);
            mctx.fill();
          }
        } else if (mode === 'smooth') {
          const boxSize = Math.max(16, bRadius * 2);
          const bx = Math.max(0, currPt.maskX - boxSize / 2);
          const by = Math.max(0, currPt.maskY - boxSize / 2);
          mctx.filter = 'blur(4px)';
          mctx.drawImage(maskCanvas, bx, by, boxSize, boxSize, bx, by, boxSize, boxSize);
          mctx.filter = 'none';
        }
        mctx.restore();

        activeItem._hasBrushEdits = true;
        const bitmap = getBitmap();
        if (bitmap) {
          fastRenderDisplay(canvasEl, bitmap, maskCanvas, settings);
        }
      };

      const applyMagicWand = (pt) => {
        const bitmap = getBitmap();
        const activeItem = getActiveItem();
        if (!pt || !bitmap || !activeItem?._bgMaskCanvas) return;

        saveUndoSnapshot(activeItem);

        const maskCanvas = activeItem._bgMaskCanvas;
        const w = maskCanvas.width;
        const h = maskCanvas.height;

        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);

        const clickX = Math.floor(Math.max(0, Math.min(w - 1, pt.maskX)));
        const clickY = Math.floor(Math.max(0, Math.min(h - 1, pt.maskY)));
        const pixel = ctx.getImageData(clickX, clickY, 1, 1).data;

        const targetR = pixel[0];
        const targetG = pixel[1];
        const targetB = pixel[2];

        const settings = getSettings();
        const tol = Number(settings.wandTolerance || 22);
        const tolSq = (tol * 3.5) * (tol * 3.5);

        const allImgData = ctx.getImageData(0, 0, w, h);
        const src = allImgData.data;

        const mctx = maskCanvas.getContext('2d');
        const maskData = mctx.getImageData(0, 0, w, h);
        const mask = maskData.data;

        for (let i = 0; i < src.length; i += 4) {
          const dr = src[i] - targetR;
          const dg = src[i + 1] - targetG;
          const db = src[i + 2] - targetB;
          const dSq = dr * dr + dg * dg + db * db;
          if (dSq <= tolSq) {
            mask[i + 3] = 0;
          }
        }

        mctx.putImageData(maskData, 0, 0);
        activeItem._hasBrushEdits = true;
        fastRenderDisplay(canvasEl, bitmap, maskCanvas, settings);
      };

      stageWrap.addEventListener('pointermove', (e) => {
        const pt = getPoints(e);
        if (!pt || (!pt.isInside && !isDrawing)) {
          clearCursorRing();
          return;
        }

        const settings = getSettings();
        const bRadius = (Number(settings.brushSize) || 30) / 2;

        if (isDrawing) {
          applyBrushStroke(pt);
          lastPt = pt;
          drawCursorRing(pt.dispX, pt.dispY, bRadius, settings.brushMode);
        } else {
          drawCursorRing(pt.dispX, pt.dispY, bRadius, settings.brushMode);
        }
      });

      stageWrap.addEventListener('pointerdown', (e) => {
        const pt = getPoints(e);
        if (!pt || !pt.isInside) return;
        const settings = getSettings();
        const activeItem = getActiveItem();

        if (settings.brushMode === 'wand') {
          applyMagicWand(pt);
        } else {
          saveUndoSnapshot(activeItem);
          isDrawing = true;
          try {
            stageWrap.setPointerCapture(e.pointerId);
          } catch {
            // Ignore if pointer capture unavailable
          }
          applyBrushStroke(pt);
          lastPt = pt;
        }
      });

      const stopDrawing = (e) => {
        if (isDrawing && e && e.pointerId) {
          try {
            stageWrap.releasePointerCapture(e.pointerId);
          } catch {
            // Ignore
          }
        }
        isDrawing = false;
        lastPt = null;
      };

      window.addEventListener('pointerup', stopDrawing);
      window.addEventListener('pointercancel', stopDrawing);

      stageWrap.addEventListener('pointerleave', () => {
        if (!isDrawing) {
          clearCursorRing();
        }
      });
    },
    process: async (file, settings, report, item) => {
      report(15, t('جارٍ قراءة الصورة الأصلية بالدقة الكاملة...', 'Reading original image...'));
      const bitmap = await openBitmap(file);
      const width = bitmap.width;
      const height = bitmap.height;

      const targetItem = item || file._itemRef || {};
      let maskCanvas = targetItem._bgMaskCanvas;

      if (!maskCanvas) {
        report(35, t('جارٍ تطبيق قناع العزل الفائق بالذكاء الاصطناعي...', 'Computing high precision AI mask...'));
        maskCanvas = await ensureItemMask(targetItem, bitmap, settings, (p, label) => {
          report(Math.round(20 + p * 0.55), label);
        });
      }

      report(80, t('جارٍ تجهيز الطبقات وتطبيق الخلفية...', 'Rendering output layers...'));
      const { canvas, context } = makeCanvas(width, height, null);

      if (settings.bgPreview === 'white') {
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, width, height);
      } else if (settings.bgPreview === 'black') {
        context.fillStyle = '#000000';
        context.fillRect(0, 0, width, height);
      } else if (settings.bgPreview === 'custom') {
        context.fillStyle = settings.bgColor || '#3b82f6';
        context.fillRect(0, 0, width, height);
      }

      const subjectCanvas = document.createElement('canvas');
      subjectCanvas.width = width;
      subjectCanvas.height = height;
      const sctx = subjectCanvas.getContext('2d');
      sctx.drawImage(bitmap, 0, 0);
      sctx.globalCompositeOperation = 'destination-in';
      sctx.drawImage(maskCanvas, 0, 0, width, height);

      context.drawImage(subjectCanvas, 0, 0);
      bitmap.close();

      report(95, t('جارٍ تصدير ملف PNG الشفاف...', 'Encoding transparent PNG...'));
      const blob = await canvasToBlob(canvas, 'image/png');

      trackEvent('tool_completed', 'background-remover');
      return {
        blob,
        filename: `${imageName(file.name)}.png`
      };
    }
  });

  // ============================================================================
  // Proactive Preloading in Background (Non-blocking / Idle callback)
  // ============================================================================
  const schedulePreload = window.requestIdleCallback || ((cb) => setTimeout(cb, 700));

  schedulePreload(() => {
    // 1. Proactively preload default AI model (RMBG-1.4) in background worker without main-thread load
    const worker = getBgWorker();
    if (worker) {
      worker.postMessage({ type: 'PRELOAD', engine: 'ai-rmbg' });
    } else {
      getRmbgPipeline((p) => {
        updateEngineCardStatus('ai-rmbg', p);
      }).then(() => {
        updateEngineCardStatus('ai-rmbg', 100);
      }).catch((err) => {
        console.warn('AI RMBG background preload note:', err);
      });
    }

    // 2. Listen to card clicks: preload / retry selected AI engine
    document.addEventListener('click', (e) => {
      const card = e.target.closest('.batch-choice-card');
      if (card) {
        const val = card.getAttribute('data-value');
        const w = getBgWorker();
        if (val === 'ai-rmbg') {
          if (w) w.postMessage({ type: 'PRELOAD', engine: 'ai-rmbg' });
          else if (!rmbgPipelinePromise) getRmbgPipeline((p) => updateEngineCardStatus('ai-rmbg', p));
        } else if (val === 'ai-modnet') {
          if (w) w.postMessage({ type: 'PRELOAD', engine: 'ai-modnet' });
          else if (!modnetPipelinePromise) getModnetPipeline((p) => updateEngineCardStatus('ai-modnet', p));
        }
      }
    });
  });
}
